import { useQuery, useReactiveVar } from '@apollo/client';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { IconButton } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useState } from 'react';
import staffStatus from 'src/api/admin/staffStatus';
import allConfigs from 'src/api/configurations/allConfigs';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import { HOME_PATH, JOBS_LIST_ENABLE, JOBS_PAGE, LIBRARY_PAGE, UPLOAD_PAGE_ENABLE } from 'src/constants';
import styles from 'src/HomePage/TopNavBar/NavBarUploadIcon/NavBarUploadIconStyles';
import viewStateVar from 'src/store/viewState';
import { useLogout } from '../../../helpers/authHelper';

interface StaffStatusData {
  me: {
    role: string;
    firstName: string;
    lastName: string;
    user: {
      username: string;
    };
  };
}

interface ConfigurationNode {
  optionValue: string;
}

interface ConfigurationEdge {
  node: ConfigurationNode;
}

interface AllConfigurationsData {
  allConfigurations: {
    edges: ConfigurationEdge[];
  };
}

interface NavBarUserNameProps {
  currentPage?: string;
}

const NavBarUserName: React.FC<NavBarUserNameProps> = ({ currentPage }) => {
  const classes = styles();
  const navigate = useNavigate();
  const [enableJobs, setEnableJobs] = useState<boolean>(false);
  const [enableUpload, setEnableUpload] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const viewState = useReactiveVar(viewStateVar);
  const { data: staffStatusData } = useQuery<StaffStatusData>(staffStatus, {
    fetchPolicy: 'cache-and-network',
  });

  // Set the user's role in viewStateVar when staffStatus data is loaded
  useEffect(() => {
    if (staffStatusData?.me?.role) {
      viewStateVar({
        ...viewStateVar(),
        signedInUserRole: staffStatusData.me.role,
      });
    }
  }, [staffStatusData]);

  useQuery<AllConfigurationsData>(allConfigs, {
    variables: {
      optionKey: JOBS_LIST_ENABLE,
    },
    onCompleted: (data) => {
      const enable = data.allConfigurations?.edges[0]?.node.optionValue;
      setEnableJobs(enable === '1');
    },
  });

  // Feature flag: enable/disable Upload page link from backend configuration
  useQuery<AllConfigurationsData>(allConfigs, {
    variables: {
      optionKey: UPLOAD_PAGE_ENABLE,
    },
    onCompleted: (data) => {
      const enable = data.allConfigurations?.edges[0]?.node.optionValue;
      setEnableUpload(enable === '1');
    },
  });

  const homePageHandle = (): void => {
    navigate({ to: HOME_PATH });
  };

  const uploadHandle = (): void => {
    navigate({ to: '/upload' });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };
  const logout = useLogout(handleClose);

  const administrationHandle = (): void => {
    navigate({ to: '/systemadmin/users' });
  };

  const jobsHandle = (): void => {
    navigate({ to: '/jobs' });
  };

  // Build actions array dynamically based on conditions
  const actions: ActionItem[] = [];
  if (currentPage !== LIBRARY_PAGE) {
    actions.push({ label: 'Library', icon: <HomeOutlinedIcon style={{ color: 'currentColor' }} /> });
  }
  if (enableUpload) {
    actions.push({ label: 'Upload Media', icon: <CloudUploadOutlinedIcon style={{ color: 'currentColor' }} /> });
  }
  if (viewState.signedInUserRole === 'ADM' || viewState.signedInUserRole === 'MGR') {
    actions.push({
      label: 'Administration',
      icon: <AdminPanelSettingsOutlinedIcon style={{ color: 'currentColor' }} />,
    });
  }
  if (enableJobs && currentPage !== JOBS_PAGE) {
    actions.push({ label: 'Jobs', icon: <WorkOutlineIcon style={{ color: 'currentColor' }} /> });
  }
  actions.push({ label: 'Logout', icon: <LogoutIcon style={{ color: 'currentColor' }} /> });

  const handleAction = (action: ActionItem): void => {
    if (action.label === 'Library') homePageHandle();
    else if (action.label === 'Upload Media') uploadHandle();
    else if (action.label === 'Administration') administrationHandle();
    else if (action.label === 'Jobs') jobsHandle();
    else if (action.label === 'Logout') logout();
    handleClose();
  };

  const userName =
    staffStatusData?.me.firstName === '' && staffStatusData?.me.lastName === ''
      ? staffStatusData?.me.user.username
      : `${staffStatusData?.me.firstName} ${staffStatusData?.me.lastName}`;

  return (
    <div className={classes.root}>
      <IconButton onClick={handleClick} size="large">
        <AccountCircleIcon color="disabled" />
      </IconButton>
      <StyledContextMenu
        actions={actions}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onAction={handleAction}
        dangerLabels={['Logout']}
        placement="bottom-end"
        id="profile-menu"
        headerContent={<span>{userName}</span>}
      />
    </div>
  );
};

export default NavBarUserName;
